import { useState } from 'react';
import {
	Button,
	ColorPicker,
	Flex,
	Form,
	Input,
	InputNumber,
	Select,
	Statistic,
	Upload,
} from 'antd';
import { LuImagePlus } from 'react-icons/lu';

import BaseText from '@components/typography/BaseText';
import { useThumbnailUrl } from '@hooks/useThumbnailUrl';
import { uploadTaxonomyThumbnail } from '@utils/api';
import { OBJECT_COLOR_PALETTE } from '@utils/constants';

export const OBJECT_FORM_MODE = Object.freeze({ CREATE: 'create', EDIT: 'edit' });

const COLOR_PRESETS = [{ label: 'Presets', colors: [...OBJECT_COLOR_PALETTE] }];

// Thumbnails are served to every user of the picker, so only plain raster
// formats the file service stores as-is are allowed. TIFF is out: the service
// treats it as an orthophoto and renames it, so each one overwrites the last.
export const THUMBNAIL_TYPES = Object.freeze({
	'image/jpeg': ['.jpg', '.jpeg'],
	'image/png': ['.png'],
	'image/webp': ['.webp'],
});
export const THUMBNAIL_MAX_BYTES = 5 * 1024 * 1024;
const THUMBNAIL_ACCEPT = Object.values(THUMBNAIL_TYPES).flat().join(',');
const THUMBNAIL_TYPE_ERROR = 'Use a .jpg, .jpeg, .png or .webp image';
const THUMBNAIL_SIZE_ERROR = 'The image must be 5 MB or smaller';
const THUMBNAIL_UPLOAD_ERROR = 'Upload failed';

// Checks the extension as well as the type: browsers report an empty or generic
// type for some files, and a renamed file can carry the wrong one.
const thumbnailProblem = file => {
	const extension = `.${String(file?.name ?? '')
		.split('.')
		.pop()
		.toLowerCase()}`;
	const allowedExtensions = THUMBNAIL_TYPES[file?.type] ?? [];
	if (!allowedExtensions.includes(extension)) return THUMBNAIL_TYPE_ERROR;
	if ((file?.size ?? 0) > THUMBNAIL_MAX_BYTES) return THUMBNAIL_SIZE_ERROR;
	return null;
};

const DEFAULT_LABEL_OPTION = { value: '', label: 'Default' };

const capitalize = text => String(text ?? '').replace(/^./, char => char.toUpperCase());

const toFileList = event => (Array.isArray(event) ? event : (event?.fileList ?? []));

/**
 * The Label choices from a node's `annotation_labels` (see the objects page's
 * `mergeAnnotationSummary`): the free-text labels annotators have used on the
 * node, and how many examples each has. The unlabelled examples (`label` null)
 * back the "Default" option, keyed by the empty string.
 *
 * @returns {{ labelOptions: string[], labelCounts: Object<string, number> }}
 */
export const labelsFromSummary = (annotationLabels = []) => {
	const labelOptions = [];
	const labelCounts = {};
	annotationLabels.forEach(({ label, annotation_count: count }) => {
		const key = label ?? '';
		if (key && !labelOptions.includes(key)) labelOptions.push(key);
		labelCounts[key] = (labelCounts[key] ?? 0) + (Number(count) || 0);
	});
	return { labelOptions, labelCounts };
};

// An existing thumbnail arrives as its storage key; antd wants a done file entry.
const toInitialFileList = (key, url) =>
	key ? [{ uid: key, name: key.split('/').pop(), status: 'done', url, response: { key } }] : [];

/**
 * Create/edit form for a taxonomy object. It derives everything it shows from
 * the node it is given, so a caller only decides the mode and what to do with
 * the result:
 *
 * - `create`: `object` is the parent the new object is trained from. The title,
 *   the Label choices and the annotation count come from it; fields start empty.
 * - `edit`: `object` is the node being edited and fills the fields; the Label
 *   choices and count come from `parent`, the examples a re-train learns from.
 *
 * A picked thumbnail is uploaded straight away through the file service and
 * only its storage key is kept. Save is blocked while an upload is running or
 * after one failed. No API call other than that upload happens here — the
 * caller runs the create or update and passes its pending state as `loading`.
 *
 * `onSubmit` receives `{ name, description, size, trained_label, color, thumbnail }`,
 * where `size` is metres (or `null`), `color` is `#RRGGBB`, `trained_label` is
 * `''` for Default, and `thumbnail` is the storage key or `null`.
 *
 * @param {'create' | 'edit'} [props.mode]
 * @param {object} props.object - the parent (create) or the node being edited (edit)
 * @param {object} [props.parent] - the edited node's parent (edit only)
 * @param {boolean} [props.loading] - the caller's mutation is pending
 * @param {(values: object) => void} props.onSubmit
 * @param {() => void} props.onCancel
 */
const ObjectForm = ({
	mode = OBJECT_FORM_MODE.CREATE,
	object,
	parent,
	loading = false,
	onSubmit,
	onCancel,
}) => {
	const isEdit = mode === OBJECT_FORM_MODE.EDIT;
	const [form] = Form.useForm();
	const thumbnailUrl = useThumbnailUrl(isEdit ? object?.thumbnail : null);

	// What the object learns from: the parent in both modes.
	const trainingSource = isEdit ? parent : object;
	const { labelOptions, labelCounts } = labelsFromSummary(trainingSource?.annotation_labels);
	const selectedLabel = Form.useWatch('trained_label', form) ?? '';
	const shownCount = trainingSource
		? (labelCounts[selectedLabel] ?? (Number(trainingSource.annotations) || 0))
		: undefined;

	const title = isEdit ? (object?.name ?? '') : `New Object from “${object?.name ?? ''}”`;
	const submitLabel = isEdit ? 'Save changes' : 'Create and train';

	const selectOptions = [
		DEFAULT_LABEL_OPTION,
		...labelOptions.map(option => ({ value: option, label: capitalize(option) })),
	];

	const initialValues = {
		name: isEdit ? (object?.name ?? '') : '',
		description: isEdit ? (object?.description ?? '') : '',
		size: isEdit ? (object?.object_size ?? null) : null,
		trained_label: isEdit ? (object?.trained_label ?? '') : '',
		color: (isEdit && object?.color) || OBJECT_COLOR_PALETTE[0],
		thumbnail: isEdit ? toInitialFileList(object?.thumbnail, thumbnailUrl) : [],
	};

	const handleFinish = ({ thumbnail, color, ...values }) => {
		onSubmit?.({
			...values,
			color: color?.toUpperCase(),
			// The uploaded or pre-existing image's key; nothing when it was removed.
			thumbnail: thumbnail?.[0]?.response?.key ?? null,
		});
	};

	// antd drives the card's progress and error states; this only bridges its
	// callbacks to the uploader and hands the key back as the response.
	const handleUploadRequest = async ({ file, onProgress, onSuccess, onError }) => {
		try {
			const key = await uploadTaxonomyThumbnail(file, percent => onProgress?.({ percent }));
			onSuccess?.({ key });
		} catch (error) {
			// The upload class rejects with the server message as a bare string, and
			// with `undefined` for a 400, so anything without text gets a plain label.
			const message = error instanceof Error ? error.message : error;
			onError?.(
				new Error(typeof message === 'string' && message ? message : THUMBNAIL_UPLOAD_ERROR)
			);
		}
	};

	// Rejected files never enter the list, so the message is shown on the field.
	const [thumbnailError, setThumbnailError] = useState(null);
	const checkThumbnail = file => {
		const problem = thumbnailProblem(file);
		setThumbnailError(problem);
		return problem ? Upload.LIST_IGNORE : true;
	};

	// Block Save while the picked image is still going up, or if it failed.
	const validateThumbnail = (_, fileList) => {
		const status = fileList?.[0]?.status;
		if (status === 'uploading')
			return Promise.reject(new Error('Wait for the upload to finish'));
		if (status === 'error')
			return Promise.reject(new Error('Upload failed, remove the image and try again'));
		return Promise.resolve();
	};

	return (
		<Form
			form={form}
			layout="horizontal"
			labelCol={{ flex: '110px' }}
			labelAlign="left"
			colon={false}
			requiredMark={false}
			initialValues={initialValues}
			onFinish={handleFinish}>
			<Flex vertical gap={12}>
				<BaseText strong>{title}</BaseText>

				{typeof shownCount === 'number' && (
					<Statistic value={shownCount} suffix="annotations" />
				)}

				<div>
					<Form.Item name="trained_label" label="Label">
						<Select options={selectOptions} />
					</Form.Item>

					<Form.Item
						name="name"
						label="Name"
						rules={[{ required: true, whitespace: true, message: 'Enter a name' }]}>
						<Input placeholder="e.g. Spruce — storm damage" />
					</Form.Item>

					<Form.Item
						name="description"
						label="Description"
						rules={[
							{ required: true, whitespace: true, message: 'Enter a description' },
						]}>
						<Input.TextArea rows={2} placeholder="What this object covers" />
					</Form.Item>

					<Form.Item name="size" label="Object size">
						<InputNumber
							min={0}
							step={0.1}
							placeholder="e.g. 5"
							addonAfter="m"
							style={{ width: '100%' }}
						/>
					</Form.Item>

					<Form.Item
						name="thumbnail"
						label="Thumbnail"
						valuePropName="fileList"
						getValueFromEvent={toFileList}
						rules={[{ validator: validateThumbnail }]}
						validateStatus={thumbnailError ? 'error' : undefined}
						help={thumbnailError ?? undefined}>
						<Upload
							listType="picture-card"
							maxCount={1}
							accept={THUMBNAIL_ACCEPT}
							beforeUpload={checkThumbnail}
							customRequest={handleUploadRequest}>
							<Flex vertical align="center" gap={4}>
								<LuImagePlus size={18} aria-hidden="true" />
								<BaseText size="xs">Upload image</BaseText>
							</Flex>
						</Upload>
					</Form.Item>

					<Form.Item
						name="color"
						label="Color"
						getValueFromEvent={color => color.toHexString()}>
						<ColorPicker format="hex" disabledAlpha showText presets={COLOR_PRESETS} />
					</Form.Item>
				</div>

				<Flex gap={8}>
					<Button onClick={onCancel} block>
						Cancel
					</Button>
					<Button type="primary" htmlType="submit" loading={loading} block>
						{submitLabel}
					</Button>
				</Flex>
			</Flex>
		</Form>
	);
};

export default ObjectForm;
